<template>
  <div class="stripe-payment-wrapper">
    <v-card outlined>
      <v-card-title>
        <v-icon left color="primary">mdi-credit-card</v-icon>
        Payment Information
      </v-card-title>

      <v-card-text>
        <div v-if="!initError">
          <v-text-field
            v-model="cardHolderName"
            label="Card Holder Name"
            placeholder="Full name on card"
            required
            outlined
            class="mb-4"
            :disabled="processing || loading"
          ></v-text-field>

          <!-- Stripe Payment Element will mount here -->
          <div id="payment-element" class="stripe-element-container">
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              <p class="mt-2">Loading payment form...</p>
            </div>
          </div>

          <v-alert v-if="paymentError" type="error" dense class="mt-4" dismissible @input="paymentError = null">
            {{ paymentError }}
          </v-alert>

          <v-overlay :value="processing" absolute>
            <div class="text-center">
              <v-progress-circular indeterminate color="white" size="64"></v-progress-circular>
              <p class="white--text mt-4">Processing payment method...</p>
            </div>
          </v-overlay>
        </div>

        <v-alert v-else type="error" class="mt-4">
          {{ initError }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="grey" @click="$emit('cancel')" :disabled="processing"> Cancel </v-btn>
        <v-btn color="primary" :disabled="!canSubmit || processing" :loading="processing" @click="handleSubmit">
          <v-icon left>mdi-credit-card-plus</v-icon>
          {{ ctaButtonText || 'Save Payment Method' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'StripeSetupForm',
  props: {
    clientSecret: {
      type: String,
      required: true
    },
    ctaButtonText: {
      type: String,
      default: 'Save Payment Method'
    },
    currentUserEmail: {
      type: String,
      default: ''
    },
    metadata: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      loading: true,
      processing: false,
      paymentError: null,
      initError: null,
      elementReady: false,
      elementComplete: false,
      cardHolderName: ''
    };
  },
  computed: {
    canSubmit() {
      return this.elementReady && this.elementComplete && !this.processing && this.cardHolderName.trim().length > 0;
    }
  },
  async mounted() {
    await this.$nextTick();
    await this.initializeStripe();
  },
  methods: {
    async initializeStripe() {
      try {
        const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
        if (!publishableKey) {
          throw new Error('Stripe publishable key is not configured');
        }

        this.stripe = await loadStripe(publishableKey);

        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#1976d2'
            }
          }
        });

        this.paymentElement = this.elements.create('payment', {
          fields: {
            billingDetails: {
              name: 'auto',
              email: 'auto'
            }
          }
        });

        await this.$nextTick();
        const mountPoint = document.getElementById('payment-element');

        if (!mountPoint) {
          throw new Error('Payment element mount point not found');
        }

        this.paymentElement.mount('#payment-element');

        this.paymentElement.on('ready', () => {
          this.elementReady = true;
          this.loading = false;
        });

        this.paymentElement.on('change', event => {
          this.elementComplete = event.complete;
          this.paymentError = event.error ? event.error.message : null;
        });
      } catch (error) {
        console.error('Stripe initialization error:', error);
        this.initError = error.message || 'Failed to initialize payment form';
        this.loading = false;
      }
    },

    async handleSubmit() {
      if (!this.canSubmit) return;

      this.processing = true;
      this.paymentError = null;

      try {
        const { setupIntent, error } = await this.stripe.confirmSetup({
          elements: this.elements,
          redirect: 'if_required',
          confirmParams: {
            return_url: window.location.href
          }
        });

        if (error) {
          throw error;
        }

        if (setupIntent) {
          this.$emit('payment-method-saved', {
            setupIntent,
            stripeCustomerId: setupIntent.customer,
            stripePaymentMethodId: setupIntent.payment_method,
            cardHolderName: this.cardHolderName
          });
        }
      } catch (error) {
        console.error('Setup error:', error);
        this.paymentError = error.message || 'Failed to save payment method. Please try again.';
        this.$emit('setup-error', error);
      } finally {
        this.processing = false;
      }
    }
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.unmount();
    }
  }
};
</script>

<style scoped>
.stripe-payment-wrapper {
  max-width: 100%;
}

.stripe-element-container {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  transition: border-color 0.2s ease;
}

.stripe-element-container:hover {
  border-color: #1976d2;
}
</style>
